//
// Label and description follow the category until the invitee edits either, so a
// team that is never renamed still reads as its own.
//
import {
  useCallback,
  useEffect,
} from 'react';

import {
  Column,
  GcatCodeIcon,
  Row,
  SelectDropdown,
  TextInput,
  useWizardContext,
} from '../../../components';
import { useTownAssets } from '../../../stores';
import { regex } from '../../../utils';

// A description short enough to say nothing identifies no one in a report
// shared between several responders
const grpDescMinLength = 10;

const gcatList = [
  'security',
  'events',
  'facilities',
  'it',
  'janitorial',
  'maintenance',
  'upperManagement',
];

const pageStateValidator = (state) => {
  const valid = {};
  const errors = {};

  valid.grpGcat = !!state.grpGcat;
  valid.grpLbl = regex.text2to45alphanum.test((state.grpLbl ?? '').trim());

  const grpDesc = (state.grpDesc ?? '').trim();
  valid.grpDescLength = (grpDesc.length >= grpDescMinLength);
  valid.grpDesc = valid.grpDescLength && regex.text100chars.test(grpDesc);

  const pageValid = valid.grpGcat && valid.grpLbl && valid.grpDesc;

  return { valid, errors, pageValid };
};

function InviteAckTeamPage() {
  const { assets } = useTownAssets();
  const { translate } = assets;
  const {
    setPageInfo,
    setPageState,
    setMustConfirmClose,
    state,
    validation,
  } = useWizardContext();

  const handleChange = useCallback(
    (event) => {
      const { name, value } = event.currentTarget;

      setMustConfirmClose(true);
      setPageState({ [name]: value, [`${name}Touched`]: true, autoGrpEdit: false });
    },
    [setPageState, setMustConfirmClose],
  );

  const handleGcatChange = useCallback(
    (event) => {
      const { value } = event.currentTarget;
      const categoryName = translate(value, 'groupCategoryCode');

      setMustConfirmClose(true);
      setPageState({
        grpGcat: value,
        grpGcatTouched: true,
        ...((state.autoGrpEdit)
          ? {
              grpLbl: categoryName,
              grpDesc: `${categoryName} ${state.cstmrLbl ?? ''}`.trim(),
            }
          : {}),
      });
    },
    [setPageState, setMustConfirmClose, translate, state.autoGrpEdit, state.cstmrLbl],
  );

  useEffect(
    () => {
      setPageInfo({
        prevPage: 'identity',
        nextPage: 'teamSize',
        pageStateValidator,
      });
    },
    [setPageInfo],
  );

  const gcatChoice = gcatList.map((thisGcatCode) => ({
    value: thisGcatCode,
    text: translate(thisGcatCode, 'groupCategoryCode'),
    icon: <GcatCodeIcon gcatCode={thisGcatCode} className="h-4 w-4" />,
  }));

  return (
    <Column crossStretch gap={4} className="flex-1 min-h-0 w-full overflow-y-auto p-2">
      <span className="px-2 mb-2 text-center">
        {`${translate(
          'defineGroupBelongsToInCstmr',
          'statements',
          { cstmr: state.cstmrLbl },
        )}:`}
      </span>

      <Row>
        <SelectDropdown
          value={state.grpGcat ?? ''}
          onChange={handleGcatChange}
          name="grpGcat"
          id="grpGcat"
          choices={gcatChoice}
          label={translate('category')}
          invalid={state.grpGcatTouched && (!validation.valid.grpGcat)}
        />
        {(state.grpGcat)
          ? <span>{translate(state.grpGcat, 'groupCategoryDesc')}</span>
          : null}
      </Row>

      <TextInput
        id="grpLbl"
        name="grpLbl"
        placeholder={translate('label')}
        className="mx-auto mt-2"
        onChange={handleChange}
        value={state.grpLbl ?? ''}
        invalid={state.grpLblTouched && (!validation.valid.grpLbl)}
        widthChars={30}
      />
      <span className="px-2 -mt-3 text-center italic text-pxTxtMuted">
        {translate('groupLblGuidelines', 'statements')}
      </span>

      <TextInput
        id="grpDesc"
        name="grpDesc"
        placeholder={translate('description')}
        className="mx-auto mt-2 max-w-full"
        onChange={handleChange}
        value={state.grpDesc ?? ''}
        invalid={state.grpDescTouched && (!validation.valid.grpDesc)}
        widthChars={50}
      />
      <span className="px-2 -mt-3 text-center italic text-pxTxtMuted">
        {translate('groupDescrGuidelines', 'statements')}
      </span>

      {(state.grpDescTouched && (!validation.valid.grpDescLength))
        ? (
            <span className="px-2 text-center text-pxUiAlert italic">
              {translate('groupDescrTooShort', 'statements', { cnt: grpDescMinLength })}
            </span>
          )
        : null}
    </Column>
  );
}

export default InviteAckTeamPage;
